'use client';

import { FC } from 'react';
import { Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { COLORS } from '@/constants';
import { isNotEmptyField } from '@/common';
import { IHousingObjectResponse } from '@/types';
import { StatItem, StatsWrapper } from './styled';

type HousingCardStatsProps = {
  prices: IHousingObjectResponse['prices'];
  squares: IHousingObjectResponse['squares'];
  otherDetails: IHousingObjectResponse['otherDetails'];
  isSell: boolean;
  inline?: boolean;
};

export const HousingCardStats: FC<HousingCardStatsProps> = ({
  prices,
  squares,
  otherDetails,
  isSell,
  inline,
}) => {
  const t = useTranslations();

  const priceLabel = isSell
    ? t('housingCardDetails.sellPriceLabel')
    : t('housingCardDetails.rentPriceLabel');

  const priceValue = (() => {
    if (!prices) return '—';
    if (isSell) {
      if (prices.sellPrice && prices.sellPrice.trim() !== '') return prices.sellPrice;
      return t('withRequest');
    }
    if (prices.monthlyCosts && prices.monthlyCosts.trim() !== '') {
      return `${prices.monthlyCosts} ${prices.monthlyCosts !== 'Preis auf Anfrage' ? t('housingCardDetails.monthly') : ''}`;
    }
    if (prices.grossRent && prices.grossRent.trim() !== '') {
      return `${prices.grossRent} ${prices.grossRent !== 'Preis auf Anfrage' ? t('housingCardDetails.monthly') : ''}`;
    }
    return t('withRequest');
  })();

  return (
    <StatsWrapper $inline={inline}>
      <StatItem>
        <Typography fontSize={12} color={COLORS['neutral/500']}>
          {priceLabel}
        </Typography>
        <Typography
          fontSize={16}
          lineHeight={1.26}
          fontWeight={600}
          color={COLORS['gray-dark-2']}
          noWrap
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {priceValue}
        </Typography>
      </StatItem>
      <StatItem>
        <Typography fontSize={12} color={COLORS['neutral/500']}>
          {t('searchResult.area')}
        </Typography>
        <Typography
          fontSize={16}
          lineHeight={1.26}
          fontWeight={600}
          color={COLORS['gray-dark-2']}
          noWrap
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {isNotEmptyField(squares?.areaDetailed)
            ? squares?.areaDetailed.replace(/ - /g, '\u00A0–\u00A0').replace(/ m²/, '\u00A0m²')
            : '—'}
        </Typography>
      </StatItem>
      <StatItem $break>
        <Typography fontSize={12} color={COLORS['neutral/500']}>
          {t('searchResult.rooms')}
        </Typography>
        <Typography fontSize={16} lineHeight={1.26} fontWeight={600} color={COLORS['gray-dark-2']}>
          {isNotEmptyField(otherDetails?.roomNumber)
            ? `${otherDetails?.roomNumber} ${t('shortRooms')}`
            : '—'}
        </Typography>
      </StatItem>
    </StatsWrapper>
  );
};
